import type { QueryKey } from '@tanstack/vue-query'

import { useQueryClientUtils } from '../useQueryClientUtils'
import { defaultFolderFieldsToInclude, useFetchFolder } from './useFetchFolder'
import {
  type QueryFolderAllCacheData,
  type QueryFolderAllUnpaginatedCacheData,
  type QueryFolderByIdCacheData,
  type QueryFolderSharedWithMeUnpaginatedCacheData,
  queryKeys,
} from './useQueryFolder'

const update = () => {
  const { update } = useFetchFolder()
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: Omit<Parameters<typeof update>[0], 'fieldsToInclude'>) => update({
      ...args,
      fieldsToInclude: defaultFolderFieldsToInclude,
    }),
    onMutate: (variables) => {
      cancelQueries([
        queryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey,
        queryKeys.all._def,
        queryKeys.allUnpaginated._def,
        queryKeys.sharedWithMeUnpaginated._def,
      ])
    },
    onSettled: (_, _1, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey,
        refetchType: 'none',
      })

      invalidateQueries([
        queryKeys.all._def,
        queryKeys.allUnpaginated._def,
        queryKeys.sharedWithMeUnpaginated._def,
      ])
    },
    onSuccess: (responseData, variables) => {
      queryClient.setQueriesData<QueryFolderByIdCacheData>(
        { queryKey: queryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey },
        (oldData) => {
          if (!oldData)
            return oldData

          return {
            ...oldData,
            data: {
              ...oldData.data,
              ...responseData.data,
            },
          }
        },
      )
    },
  })
}

interface DropMutationContext {
  previousAll: Array<[QueryKey, QueryFolderAllCacheData | undefined]>
  previousAllUnpaginated: Array<[QueryKey, QueryFolderAllUnpaginatedCacheData | undefined]>
  previousSharedWithMeUnpaginated: Array<[QueryKey, QueryFolderSharedWithMeUnpaginatedCacheData | undefined]>
}

const drop = () => {
  const { drop: mutationFn } = useFetchFolder()
  const { invalidateQueries } = useQueryClientUtils()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onMutate: async (variables): Promise<DropMutationContext> => {
      const byIdQueryKey = queryKeys.byId({
        id: variables.id,
        workspaceId: variables.workspaceId,
      }).queryKey

      await Promise.all([
        queryClient.cancelQueries({ queryKey: byIdQueryKey }),
        queryClient.cancelQueries({ queryKey: queryKeys.all._def }),
        queryClient.cancelQueries({ queryKey: queryKeys.allUnpaginated._def }),
        queryClient.cancelQueries({ queryKey: queryKeys.sharedWithMeUnpaginated._def }),
      ])

      const previousAll = queryClient.getQueriesData<QueryFolderAllCacheData>({
        queryKey: queryKeys.all._def,
      })
      const previousAllUnpaginated = queryClient.getQueriesData<QueryFolderAllUnpaginatedCacheData>({
        queryKey: queryKeys.allUnpaginated._def,
      })
      const previousSharedWithMeUnpaginated = queryClient.getQueriesData<QueryFolderSharedWithMeUnpaginatedCacheData>({
        queryKey: queryKeys.sharedWithMeUnpaginated._def,
      })

      queryClient.setQueriesData<QueryFolderAllCacheData>({
        queryKey: queryKeys.all._def,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: oldData.data.filter(item => item.id !== variables.id),
        }
      })

      queryClient.setQueriesData<QueryFolderAllUnpaginatedCacheData>({
        queryKey: queryKeys.allUnpaginated._def,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: oldData.data.filter(item => item.id !== variables.id),
        }
      })

      queryClient.setQueriesData<QueryFolderSharedWithMeUnpaginatedCacheData>({
        queryKey: queryKeys.sharedWithMeUnpaginated._def,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: oldData.data.filter(item => item.id !== variables.id),
        }
      })

      return { previousAll, previousAllUnpaginated, previousSharedWithMeUnpaginated }
    },
    onError: (_error, _variables, context) => {
      context?.previousAll.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context?.previousAllUnpaginated.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context?.previousSharedWithMeUnpaginated.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_, _1, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey,
      })

      invalidateQueries([
        queryKeys.all._def,
        queryKeys.allUnpaginated._def,
        queryKeys.sharedWithMeUnpaginated._def,
      ])
    },
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: queryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey,
      })
    },
  })
}

const create = () => {
  const { create: mutationFn } = useFetchFolder()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn,
    onMutate: () => {
      cancelQueries([
        queryKeys.all._def,
        queryKeys.allUnpaginated._def,
        queryKeys.sharedWithMeUnpaginated._def,
      ])
    },
    onSettled: () => {
      invalidateQueries([
        queryKeys.all._def,
        queryKeys.allUnpaginated._def,
        queryKeys.sharedWithMeUnpaginated._def,
      ])
    },
  })
}

export const useMutationFolder = {
  create,
  update,
  drop,
}
